import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './AdminInquiries.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrash, FaEye, FaSearch, FaFilter, FaTimes, FaCalendarAlt, FaEnvelope, FaUser, FaBuilding, FaProjectDiagram, FaEnvelopeOpenText } from 'react-icons/fa';
import toast from 'react-hot-toast';

import { safeFetch } from '../utils/apiUtils';

const AdminInquiries = () => {
    const [inquiries, setInquiries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchInquiries();
    }, []);

    const fetchInquiries = async () => {
        setIsLoading(true);
        try {
            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/consultations', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });

            if (ok) {
                // Adjust based on typical API response structure (direct array or result.data)
                setInquiries(Array.isArray(result) ? result : result.data || []);
            } else {
                toast.error(result.message || 'Failed to fetch inquiries');
            }
        } catch (error) {
            console.error('Error fetching inquiries:', error);
            toast.error('Network error while fetching inquiries');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to remove this inquiry?')) return;

        try {
            const { ok, data: result } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/consultations/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });

            if (ok) {
                setInquiries(inquiries.filter(i => i.id !== id));
                toast.success('Inquiry deleted successfully');
                if (selectedInquiry?.id === id) setSelectedInquiry(null);
            } else {
                toast.error(result.message || 'Failed to delete inquiry');
            }
        } catch (error) {
            console.error('Error deleting inquiry:', error);
            toast.error('Network error while deleting');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const filteredInquiries = inquiries.filter(i =>
        (i.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (i.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (i.company_name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>User <span>Inquiries</span></h1>
                        <p>Track and manage client requests submitted through the portal.</p>
                    </div>
                </header>

                <div className="inquiry-controls">
                    <div className="search-bar">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name, email or company..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="filter-badge">
                        <FaFilter /> Total: {filteredInquiries.length}
                    </div>
                    <button className="refresh-btn" onClick={fetchInquiries} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #e2e8f0', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}>
                        Refresh
                    </button>
                </div>

                <div className="inquiries-list">
                    {isLoading ? (
                        <div className="admin-loading-container" style={{ textAlign: 'center', padding: '50px' }}>
                            <div className="admin-loader"></div>
                            <p style={{ marginTop: '20px', color: '#64748b' }}>Loading inquiries...</p>
                        </div>
                    ) : filteredInquiries.length > 0 ? (
                        filteredInquiries.map((inquiry, index) => (
                            <motion.div
                                key={inquiry.id}
                                className={`inquiry-row ${inquiry.is_new ? 'new' : ''}`}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <div className="inquiry-main">
                                    <div className="user-avatar">
                                        {inquiry.full_name?.charAt(0) || '?'}
                                    </div>
                                    <div className="inquiry-info">
                                        <h3>{inquiry.full_name}</h3>
                                        <div className="meta">
                                            <span><FaEnvelope /> {inquiry.email}</span>
                                            <span><FaBuilding /> {inquiry.company_name || 'Individual'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="inquiry-type">
                                    <span className="type-badge">{inquiry.project_type}</span>
                                </div>

                                <div className="inquiry-date">
                                    <FaCalendarAlt /> {formatDate(inquiry.created_at)}
                                </div>

                                <div className="inquiry-actions">
                                    <button className="view-btn" onClick={() => setSelectedInquiry(inquiry)} title="View Full Message">
                                        <FaEye />
                                    </button>
                                    <button className="del-btn" onClick={() => handleDelete(inquiry.id)} title="Delete Inquiry">
                                        <FaTrash />
                                    </button>
                                </div>
                            </motion.div>
                        ))
                    ) : (
                        <div className="empty-state">
                            <FaEnvelopeOpenText />
                            <h3>No inquiries found</h3>
                            <p>All clear! There are no client requests at the moment.</p>
                        </div>
                    )}
                </div>

                {/* Inquiry Detail Modal */}
                <AnimatePresence>
                    {selectedInquiry && (
                        <motion.div
                            className="modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="inquiry-detail-modal"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                            >
                                <div className="modal-header">
                                    <h2>Inquiry <span>Details</span></h2>
                                    <button className="close-modal" onClick={() => setSelectedInquiry(null)}>
                                        <FaTimes />
                                    </button>
                                </div>

                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label><FaUser /> Name</label>
                                        <p>{selectedInquiry.full_name}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label><FaEnvelope /> Work Email</label>
                                        <p>{selectedInquiry.email}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label><FaBuilding /> Company</label>
                                        <p>{selectedInquiry.company_name || 'Individual'}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label><FaProjectDiagram /> Project Type</label>
                                        <p>{selectedInquiry.project_type}</p>
                                    </div>
                                </div>

                                <div className="detail-message">
                                    <label>Message</label>
                                    <div className="message-box">
                                        {selectedInquiry.message}
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button className="secondary-btn" onClick={() => setSelectedInquiry(null)} style={{ marginRight: '10px', background: '#f1f5f9', border: 'none', padding: '12px 25px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                                        Close
                                    </button>
                                    <button className="primary-btn" onClick={() => {
                                        window.location.href = `mailto:${selectedInquiry.email}?subject=Regarding your ${selectedInquiry.project_type} inquiry`;
                                        setSelectedInquiry(null);
                                    }}>
                                        Reply via Email
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default AdminInquiries;
